/**
 * FadeRestore — найти затухание, которое уже сведено в запись, и вернуть
 * громкость под ним.
 *
 * Зачем
 * ─────
 * Обращение 18.10.2026 (/volume, iPhone): «мне нужно в конце убрать затухание».
 * Человек 15 минут пытался сделать это «Участками» — девять участков +20 дБ на
 * последние 5–20 секунд — и не мог: затухание идёт КРИВОЙ, а участок — ступень
 * (+20 дБ клиппирует начало затухания), и перекрытия участков не складываются.
 * На форумах ответ один — «запечённое затухание не убрать». Замер говорит иначе.
 *
 * Замеры 2026-10-20 (34 песни корпуса ai-styles, 40–60 с из середины = истина,
 * затухания linear / dB-linear / cos длиной 6–30 с, MP3 192/128, 16-битный мастер)
 * ───────────────────────────────────────────────────────────────────────────
 *  • MP3 тихое НЕ портит: с ТОЧНОЙ обратной кривой звук возвращается без потерь
 *    до −50 дБ — отношение сигнал/шум такое же, как у MP3 на полной громкости
 *    (25 дБ на 192), верх 10–16 кГц цел. 16-битный мастер мешает с −54 дБ
 *    (21.8 против 25). Отсюда MAX_BOOST_DB.
 *  • Кривая угадывается по самой музыке. Пять оценщиков, ошибка уровня
 *    результата (окна 1 с, затухание ≤ 48 дБ):
 *
 *      энергия, окно 3 с            |e| медиана 1.05 дБ, p90 1.93, смещение −0.79
 *      дБ 0.4 с + медиана 2 с        0.92 / 1.77 / −0.65
 *      то же, полоса 100–6000 Гц     1.02 / 2.05 / −0.56
 *      дБ 0.4 с + медиана 4 с        0.87 / 1.73 / −0.60
 *      + сглаживание 1 с + плато
 *        по самой кривой            0.81 / 1.55 / −0.48   ← здесь
 *
 *    Колебание уровня 0.26 дБ за полсекунды — ниже слышимого. Лимитер мастеринга
 *    ПОСЛЕ затухания картину не меняет (0.80 / 1.68). Энергию окном 3 с брать
 *    нельзя: на спаде среднее энергии смещено вверх (неравенство Йенсена).
 *  • ⚠️ Главная ловушка — НАСТОЯЩИЕ концовки. Наивный детектор «нашёл
 *    затухание» в 33 песнях из 34: почти все они кончаются звоном последнего
 *    аккорда. Вытянуть звон = застывший аккорд с поднятым шумом. Отличает
 *    затухание то, что МУЗЫКА ПРОДОЛЖАЕТСЯ: новые атаки (поток лог-спектра,
 *    нормированного по кадру, — громкость он не видит) идут до самого дна и с
 *    прежней частотой. Сетка порогов на 63 отрицательных (26 размеченных по
 *    спектрограммам концовок, 3 спорных, 34 куска без затухания) и 408
 *    положительных:
 *
 *      длина ≥ 7 с, атаки до ≥ 90 % спада, их темп ≥ 0.6 прежнего
 *        ложных срабатываний 0 из 63; найдено затуханий 10 с — 95 %,
 *        20 с — 98 %, 30 с — 98 %, 6 с — 62 %
 *
 *    Центроид спектра проверен и отброшен: шумит в обе стороны.
 *    «Затухания» Suno детектор не берёт — и правильно: спектр у них темнеет
 *    (центроид ×0.12), это сгенерированное затихание, а не уменьшенная
 *    громкость, и обратная кривая его не вернула бы.
 *
 * Контракт
 * ────────
 *   detect(channels, sampleRate) → результат с `found` и признаками (синхронно)
 *   restorePoints(det, {endT, releaseSec}) → точки [{t, g}] множителя огибающей:
 *     1 до начала затухания, обратная кривая до endT, линейный спуск к 0 за
 *     releaseSec, 0 дальше. Точек мало (допуск интерполяции 0.1 дБ), потому что
 *     FFmpeg-лейн считает выражение на каждый сэмпл.
 *
 * Лаборатория (Python, scratchpad сессии 2026-10-20) — память tool_volume.
 * Перенос сверен на тех же 476 случаях: tests/js/fade-restore.test.mjs.
 */
(function (root) {
    'use strict';

    var HOP_S = 0.05;          // шаг ряда уровня
    var WIN_S = 0.4;           // окно уровня
    var LOOK_S = 60;           // сколько секунд с конца смотрим
    var MEDIAN_S = 4;          // бегущая медиана: такты и доли не должны просочиться в кривую
    var SMOOTH_S = 1;          // сглаживание монотонной кривой
    var SLOPE_MIN_DB_S = 0.2;  // начало спада: громкость падает хотя бы так быстро…
    var SLOPE_SPAN_S = 2;      // …на отрезке такой длины…
    var SLOPE_GAP_S = 1;       // …с перерывами не длиннее этого
    var MIN_DROP_DB = 20;      // спад к концу глубже 20 дБ
    var MIN_LEN_S = 7;         // концовки корпуса — 4–8 с звона; затухание длиннее
    var MIN_COVERAGE = 0.9;    // атаки идут до 90 % спада
    var MIN_RATE = 0.6;        // и не реже 0.6 прежнего темпа
    var DEEP_DB = 30;          // глубже — сигнал у пола, атаки там уже не видны
    var PRE_S = 8;             // окно «до затухания» для сравнения атак
    var END_DB = 40;           // конец музыки по умолчанию — где затухание дошло до −40 дБ
    var MAX_BOOST_DB = 48;     // дальше −50 дБ 16-битный мастер портит звук (замер)
    var TOL_DB = 0.1;          // допуск прореживания точек
    var MIN_RELEASE_S = 0.05;  // «обрыв» — тоже спуск, иначе щелчок

    function dbToLin(db) { return Math.pow(10, db / 20); }

    /* ───────────────────────── ряд уровня ───────────────────────── */

    function monoSlice(channels, a, b) {
        var n = Math.max(0, b - a), out = new Float64Array(n), nCh = channels.length;
        for (var c = 0; c < nCh; c++) {
            var d = channels[c];
            for (var i = 0; i < n; i++) out[i] += d[a + i];
        }
        if (nCh > 1) for (var j = 0; j < n; j++) out[j] /= nCh;
        return out;
    }

    /** Уровень в дБ каждые HOP_S: средняя энергия в окне WIN_S вокруг точки. */
    function levelSeries(mono, sr, offsetS) {
        var h = Math.max(1, Math.round(HOP_S * sr));
        var nb = Math.floor(mono.length / h);
        var be = new Float64Array(nb);
        for (var k = 0; k < nb; k++) {
            var s = 0;
            for (var i = k * h, e = i + h; i < e; i++) s += mono[i] * mono[i];
            be[k] = s / h;
        }
        var half = Math.max(1, Math.round(WIN_S / HOP_S / 2));
        var t = new Float64Array(nb), L = new Float64Array(nb);
        for (var q = 0; q < nb; q++) {
            var acc = 0, cnt = 0;
            for (var r = q - half; r < q + half; r++) {
                acc += be[r < 0 ? 0 : (r >= nb ? nb - 1 : r)]; cnt++;
            }
            t[q] = offsetS + q * h / sr;
            L[q] = 10 * Math.log10(Math.max(acc / cnt, 1e-14));
        }
        return { t: t, L: L };
    }

    function medianFilter(x, size) {
        var n = x.length, half = size >> 1, out = new Float64Array(n), buf = new Float64Array(size);
        for (var i = 0; i < n; i++) {
            for (var k = -half; k <= half; k++) {
                var j = i + k; buf[k + half] = x[j < 0 ? 0 : (j >= n ? n - 1 : j)];
            }
            var s = buf.slice().sort();   // типизированный массив сортирует числа сам
            out[i] = s[half];
        }
        return out;
    }

    /** Монотонная невозрастающая аппроксимация (pool adjacent violators). */
    function isotonicDecreasing(y) {
        var vals = [], wts = [], cnt = [];
        for (var i = 0; i < y.length; i++) {
            vals.push(-y[i]); wts.push(1); cnt.push(1);
            while (vals.length > 1 && vals[vals.length - 2] > vals[vals.length - 1]) {
                var v2 = vals.pop(), w2 = wts.pop(), c2 = cnt.pop();
                var v1 = vals.pop(), w1 = wts.pop(), c1 = cnt.pop();
                vals.push((v1 * w1 + v2 * w2) / (w1 + w2)); wts.push(w1 + w2); cnt.push(c1 + c2);
            }
        }
        var out = new Float64Array(y.length), p = 0;
        for (var b = 0; b < vals.length; b++) for (var c = 0; c < cnt[b]; c++) out[p++] = -vals[b];
        return out;
    }

    /** Скользящее среднее чётной/нечётной длины с повтором краёв (как scipy 'nearest'). */
    function movingAverage(x, size) {
        var n = x.length, out = new Float64Array(n), lo = size >> 1, hi = size - lo - 1;
        for (var i = 0; i < n; i++) {
            var s = 0;
            for (var k = -lo; k <= hi; k++) { var j = i + k; s += x[j < 0 ? 0 : (j >= n ? n - 1 : j)]; }
            out[i] = s / size;
        }
        return out;
    }

    /** Идём от конца назад, пока громкость устойчиво падает. */
    function fadeStartIndex(f) {
        var k = Math.round(SLOPE_SPAN_S / HOP_S), i = f.length - 1, flat = 0, start = i;
        while (i - k >= 0) {
            var slope = (f[i - k] - f[i]) / SLOPE_SPAN_S;
            if (slope >= SLOPE_MIN_DB_S) { start = i - k; flat = 0; }
            else { flat++; if (flat * HOP_S > SLOPE_GAP_S) break; }
            i--;
        }
        return start;
    }

    /* ───────────────────────── атаки ───────────────────────── */

    function fftSizeFor(sr) {
        var n = 1;
        while (n * 2 <= 0.0464 * sr * 1.5) n *= 2;   // ~46 мс: 2048 на 44.1/48 кГц
        return Math.max(256, n);
    }

    /**
     * Сканер атак: пики потока лог-спектра, нормированного по кадру (громкость
     * он не видит). Кадры идут ПАРАМИ — два вещественных кадра в одном
     * комплексном БПФ (z = x + i·y), спектры разбираются по эрмитовой
     * симметрии; мощность вместо модуля — без квадратного корня.
     *
     * step(maxPairs) считает порцию и отвечает, кончились ли кадры: страница
     * уступает поток между порциями, тест зовёт всё сразу.
     */
    function onsetScanner(mono, sr, offsetS, getFft) {
        var N = fftSizeFor(sr), H = N >> 2;
        var nb = Math.min((N >> 1) + 1, Math.floor(15000 * N / sr));
        var fftApi = getFft(N);
        if (!fftApi) return null;
        var win = new Float64Array(N);
        for (var w = 0; w < N; w++) win[w] = 0.5 - 0.5 * Math.cos(2 * Math.PI * w / (N - 1));
        var re = new Float64Array(N), im = new Float64Array(N);
        var prev = null, cur = new Float64Array(nb), cur2 = new Float64Array(nb);
        var flux = [], ft = [];
        var a = 0, total = mono.length;
        var LOG = 10 / Math.LN10;

        function frameDb(buf, isY) {
            var mx = -Infinity;
            for (var k = 0; k < nb; k++) {
                var pw;
                if (k === 0) pw = isY ? im[0] * im[0] : re[0] * re[0];
                else {
                    var m = N - k, ar = re[k], ai = im[k], br = re[m], bi = im[m];
                    pw = isY ? ((ai + bi) * (ai + bi) + (br - ar) * (br - ar)) * 0.25
                             : ((ar + br) * (ar + br) + (ai - bi) * (ai - bi)) * 0.25;
                }
                var db = LOG * Math.log(pw + 1e-18);
                buf[k] = db; if (db > mx) mx = db;
            }
            for (var k2 = 0; k2 < nb; k2++) { var v = buf[k2] - mx; buf[k2] = v < -50 ? -50 : v; }
        }
        function push(buf, aa) {
            if (prev) {
                var s = 0;
                for (var b = 0; b < nb; b++) { var d = buf[b] - prev[b]; if (d > 0) s += d; }
                flux.push(s / nb); ft.push(offsetS + (aa + N / 2) / sr);
            }
        }
        return {
            step: function (maxPairs) {
                for (var pr = 0; pr < maxPairs; pr++) {
                    if (a + N > total) return true;
                    var hasB = a + H + N <= total;
                    for (var i = 0; i < N; i++) {
                        re[i] = mono[a + i] * win[i];
                        im[i] = hasB ? mono[a + H + i] * win[i] : 0;
                    }
                    fftApi(re, im);
                    frameDb(cur, false);
                    push(cur, a);
                    var t1 = prev || new Float64Array(nb); prev = cur; cur = t1;
                    if (hasB) {
                        frameDb(cur2, true);
                        push(cur2, a + H);
                        var t2 = prev; prev = cur2; cur2 = t2;
                    }
                    a += hasB ? 2 * H : H;
                }
                return a + N > total;
            },
            onsets: function (refEndS) {
                var pre = [];
                for (var p = 0; p < flux.length; p++) if (ft[p] < refEndS) pre.push(flux[p]);
                var thr;
                if (pre.length > 10) {
                    var sorted = pre.slice().sort(function (x, y) { return x - y; });
                    var med = sorted.length % 2 ? sorted[sorted.length >> 1] : (sorted[sorted.length / 2 - 1] + sorted[sorted.length / 2]) / 2;
                    var mean = 0; for (var q = 0; q < pre.length; q++) mean += pre[q]; mean /= pre.length;
                    var vr = 0; for (var q2 = 0; q2 < pre.length; q2++) vr += (pre[q2] - mean) * (pre[q2] - mean);
                    thr = med + 2 * Math.sqrt(vr / pre.length);
                } else {
                    var all = flux.slice().sort(function (x, y) { return x - y; });
                    thr = 2 * (all.length ? all[all.length >> 1] : 0);
                }
                var out = [];
                for (var j = 1; j + 1 < flux.length; j++) {
                    if (flux[j] > flux[j - 1] && flux[j] >= flux[j + 1] && flux[j] > thr) out.push(ft[j]);
                }
                return out;
            }
        };
    }

    var _fftCache = {};
    function defaultFft(N) {
        if (_fftCache[N]) return _fftCache[N];
        var M = root && root.DspFftMixed;
        if (!M || typeof M.plan !== 'function') return null;
        var p = M.plan(N);
        if (!p) return null;
        _fftCache[N] = function (re, im) { M.fft(p, re, im); };
        return _fftCache[N];
    }

    /* ───────────────────────── детектор ───────────────────────── */

    /** Первая, дешёвая ступень: ряд уровня, монотонная кривая, начало спада.
     *  Отвечает «не затухание» сама или отдаёт контекст для атак. */
    function levelStage(channels, sr) {
        var n = channels && channels[0] ? channels[0].length : 0;
        var dur = n / sr;
        var res = { found: false, reason: '', duration: dur };
        if (!n || dur < MIN_LEN_S + PRE_S) { res.reason = 'short'; return { res: res }; }

        var from = Math.max(0, n - Math.round(LOOK_S * sr));
        var ser = levelSeries(monoSlice(channels, from, n), sr, from / sr);
        var t = ser.t, L = ser.L, m = L.length;
        if (m < 3 * Math.round(SLOPE_SPAN_S / HOP_S)) { res.reason = 'short'; return { res: res }; }

        var Lm = medianFilter(L, Math.round(MEDIAN_S / HOP_S) | 1);
        var f = movingAverage(isotonicDecreasing(Lm), Math.round(SMOOTH_S / HOP_S));
        var i0 = fadeStartIndex(f);
        var P = f[i0], end = t[m - 1];
        var drop = P - f[m - 1];
        var g = new Float64Array(m);
        for (var i = i0; i < m; i++) g[i] = Math.min(f[i] - P, 0);
        var i30 = m - 1;
        for (var j = i0; j < m; j++) if (g[j] <= -DEEP_DB) { i30 = j; break; }
        var tStart = t[i0], t30 = g[i30] <= -DEEP_DB ? t[i30] : end;

        res.t0 = tStart; res.lenS = end - tStart; res.dropDb = drop;
        res.series = { t: t, g: g, i0: i0 };
        if (drop < MIN_DROP_DB) { res.reason = 'shallow'; return { res: res }; }
        if (res.lenS < MIN_LEN_S) { res.reason = 'too_short'; return { res: res }; }

        // Атаки — только на [начало − 8 с, −30 дБ]: глубже они не учитываются,
        // а БПФ — самая дорогая часть детектора.
        var a = Math.max(0, Math.round((tStart - PRE_S) * sr));
        var b = Math.min(n, Math.round((t30 + 0.2) * sr));
        return { res: res, ctx: { a: a, b: b, tStart: tStart, t30: t30, i0: i0, m: m, t: t, g: g, end: end } };
    }

    function finishStage(res, ctx, on) {
        var tStart = ctx.tStart, t30 = ctx.t30, t = ctx.t, g = ctx.g, i0 = ctx.i0, m = ctx.m, end = ctx.end;
        var inside = 0, lastOn = tStart, before = 0;
        for (var k = 0; k < on.length; k++) {
            var ot = on[k];
            if (ot >= tStart && ot < t30) { inside++; if (ot > lastOn) lastOn = ot; }
            else if (ot < tStart && ot >= tStart - PRE_S) before++;
        }
        var span = Math.max(t30 - tStart, 0.5);
        res.coverage = (lastOn - tStart) / span;
        res.rateRatio = (inside / span) / Math.max(before / PRE_S, 0.25);
        if (res.coverage < MIN_COVERAGE) { res.reason = 'music_stops'; return res; }
        if (res.rateRatio < MIN_RATE) { res.reason = 'music_thins'; return res; }

        // Где кончать музыку: по умолчанию там, где затухание дошло до −40 дБ;
        // дальше −48 дБ двигать нельзя (16-битный пол).
        var iEnd = m - 1, iMax = m - 1;
        for (var q = i0; q < m; q++) if (-g[q] >= END_DB) { iEnd = q; break; }
        for (var q2 = i0; q2 < m; q2++) if (-g[q2] >= MAX_BOOST_DB) { iMax = q2; break; }
        res.found = true;
        res.reason = 'fade';
        res.tEnd = Math.min(t[iEnd], end);
        res.tMax = Math.min(t[iMax], end);
        res.depthAtEndDb = -g[iEnd];
        return res;
    }

    /**
     * @param channels  массивы Float32Array (каналы AudioBuffer)
     * @param sr        частота
     * @param opts.fft  (N) → fn(re, im) — для тестов вне браузера
     */
    function detect(channels, sr, opts) {
        opts = opts || {};
        var clock = Date.now();
        var st = levelStage(channels, sr);
        if (!st.ctx) { st.res.ms = Date.now() - clock; return st.res; }
        var sc = onsetScanner(monoSlice(channels, st.ctx.a, st.ctx.b), sr, st.ctx.a / sr, opts.fft || defaultFft);
        if (!sc) { st.res.reason = 'no_fft'; st.res.ms = Date.now() - clock; return st.res; }
        while (!sc.step(1e9)) { /* всё сразу */ }
        var res = finishStage(st.res, st.ctx, sc.onsets(st.ctx.tStart));
        res.ms = Date.now() - clock;
        return res;
    }

    /** То же, но уступая поток между порциями кадров: страница не замирает.
     *  opts.cancelled() → true прекращает работу (новый файл), ответ — null. */
    function detectAsync(channels, sr, opts) {
        opts = opts || {};
        var clock = Date.now();
        var pause = opts.yieldFn || function () { return new Promise(function (r) { setTimeout(r, 0); }); };
        return Promise.resolve().then(function () {
            var st = levelStage(channels, sr);
            if (!st.ctx) { st.res.ms = Date.now() - clock; return st.res; }
            var sc = onsetScanner(monoSlice(channels, st.ctx.a, st.ctx.b), sr, st.ctx.a / sr, opts.fft || defaultFft);
            if (!sc) { st.res.reason = 'no_fft'; st.res.ms = Date.now() - clock; return st.res; }
            function loop() {
                if (opts.cancelled && opts.cancelled()) return null;
                if (!sc.step(64)) return pause().then(loop);
                var res = finishStage(st.res, st.ctx, sc.onsets(st.ctx.tStart));
                res.ms = Date.now() - clock;
                return res;
            }
            return loop();
        });
    }

    /* ───────────────────── точки множителя огибающей ───────────────────── */

    /** Прореживание: оставить точки так, чтобы линейная интерполяция отличалась
     *  от кривой не больше чем на TOL_DB (в дБ относительно значения кривой). */
    function simplify(ts, gs, tolDb, absFloor) {
        var floor = absFloor || 0;
        var keep = [0, ts.length - 1];
        var stack = [[0, ts.length - 1]];
        while (stack.length) {
            var seg = stack.pop(), a = seg[0], b = seg[1];
            if (b - a < 2) continue;
            var worst = -1, wi = -1;
            for (var i = a + 1; i < b; i++) {
                var u = (ts[i] - ts[a]) / (ts[b] - ts[a]);
                var lin = gs[a] + (gs[b] - gs[a]) * u;
                // Ниже порога (−40 дБ от музыки) ошибка меряется абсолютно, выше — в дБ.
                var err = (lin < floor && gs[i] < floor)
                    ? (Math.abs(lin - gs[i]) > floor ? tolDb * 2 : 0)
                    : Math.abs(20 * Math.log10((lin + 1e-9) / (gs[i] + 1e-9)));
                if (err > worst) { worst = err; wi = i; }
            }
            if (worst > tolDb) { keep.push(wi); stack.push([a, wi], [wi, b]); }
        }
        keep.sort(function (x, y) { return x - y; });
        var out = [];
        for (var k = 0; k < keep.length; k++) out.push({ t: ts[keep[k]], g: gs[keep[k]] });
        return out;
    }

    /**
     * Где на самом деле кончается музыка: спуск обязан ПОМЕСТИТЬСЯ в файл.
     * Стенд 2026-10-20: затухание, не дошедшее до −40 дБ к концу файла, давало
     * концовку ровно на последней секунде — и трёхсекундный спуск обрезался
     * в обрыв на полной громкости.
     */
    function endFor(det, endT, releaseSec) {
        var rel = Math.max(MIN_RELEASE_S, Number(releaseSec) || 0);
        var e = Math.min(Math.max(Number(endT) || det.tEnd, det.t0 + 0.5), det.tMax);
        e = Math.max(det.t0 + 0.5, Math.min(e, det.duration - rel));
        return { endT: e, stopT: Math.min(e + rel, det.duration), releaseSec: rel, maxEndT: Math.max(det.t0 + 0.5, Math.min(det.tMax, det.duration - rel)) };
    }

    /**
     * @param det        результат detect() с found
     * @param o.endT     где кончается музыка (по умолчанию det.tEnd)
     * @param o.releaseSec спуск к нулю после endT (0 → MIN_RELEASE_S)
     */
    function restorePoints(det, o) {
        o = o || {};
        if (!det || !det.found || !det.series) return null;
        var s = det.series, t = s.t, g = s.g, i0 = s.i0;
        var ef = endFor(det, o.endT, o.releaseSec);
        var endT = ef.endT, stopT = ef.stopT;
        var boostAt = function (k) { return dbToLin(Math.min(-g[k], MAX_BOOST_DB)); };

        var at = function (tt) { return boostAt(Math.min(t.length - 1, Math.max(i0, Math.round((tt - t[0]) / HOP_S)))); };

        // Обратная кривая до endT, затем спуск. Под спуском затухание записи
        // продолжается, поэтому кривая работает и там — иначе «3 секунды»
        // спуска вышли бы обрывом глубже. Всё — на сетке ряда (0.05 с), потом
        // прореживание по допуску: произведение «кривая × линейный спуск» не
        // линейно, и число отрезков решает допуск, а не догадка.
        var ts = [], gs = [];
        for (var k = i0; k < t.length && t[k] < endT; k++) { ts.push(t[k]); gs.push(boostAt(k)); }
        ts.push(endT); gs.push(at(endT));
        var gEnd = gs[gs.length - 1];
        var span = stopT - endT, nStep = Math.max(1, Math.ceil(span / HOP_S));
        for (var q = 1; q <= nStep; q++) {
            var u = q / nStep, tq = endT + span * u;
            ts.push(tq); gs.push(q === nStep ? 0 : at(tq) * (1 - u));
        }
        // У нуля относительная ошибка бесконечна — там допуск абсолютный:
        // на −40 дБ ниже уровня музыки в момент конца.
        var pts = simplify(ts, gs, TOL_DB, gEnd * 0.01);
        pts[0].g = 1;
        return pts;
    }

    var api = {
        detect: detect,
        detectAsync: detectAsync,
        restorePoints: restorePoints,
        endFor: endFor,
        MAX_BOOST_DB: MAX_BOOST_DB,
        END_DB: END_DB,
        _internals: { levelSeries: levelSeries, medianFilter: medianFilter, isotonicDecreasing: isotonicDecreasing, movingAverage: movingAverage, fadeStartIndex: fadeStartIndex, onsetScanner: onsetScanner, simplify: simplify }
    };
    if (root) root.FadeRestore = api;
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
})(typeof window !== 'undefined' ? window : (typeof globalThis !== 'undefined' ? globalThis : null));
